import { useEffect, RefObject } from 'react';

/**
 * Swipe from left edge to go back — iOS-style back gesture.
 * Triggers `onBack` when the user swipes right from the left 30px of the screen.
 *
 * @param ref - Ref to the container element (usually the page wrapper)
 * @param onBack - Callback to navigate back
 * @param enabled - Whether the gesture is active (default true)
 */
export function useSwipeBack(
  ref: RefObject<HTMLElement | null>,
  onBack: () => void,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let swiping = false;

    const EDGE_WIDTH = 30; // px from left edge to trigger
    const THRESHOLD = 80;  // px swipe distance to commit
    const SPRING = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';

    const onStart = (e: TouchEvent) => {
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      // Only start tracking if touch begins in the left edge zone
      if (x <= EDGE_WIDTH) {
        startX = x;
        startY = y;
        tracking = true;
        swiping = false;
      }
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - startX;
      const dy = Math.abs(e.touches[0].clientY - startY);

      // If vertical movement dominates, cancel — user is scrolling
      if (dy > 30 && dx < dy) {
        tracking = false;
        el.style.transform = '';
        el.style.transition = '';
        return;
      }

      if (dx > 10) {
        swiping = true;
        // Diminishing resistance
        const progress = Math.min(dx / THRESHOLD, 1);
        const shift = dx * 0.6;
        el.style.transition = 'none';
        el.style.transform = `translateX(${shift}px)`;
        el.style.opacity = `${1 - progress * 0.3}`;
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (!tracking) return;
      const dx = (e.changedTouches?.[0]?.clientX ?? 0) - startX;
      tracking = false;

      if (swiping && dx >= THRESHOLD) {
        // Commit — slide fully off-screen then call back
        el.style.transition = SPRING;
        el.style.transform = 'translateX(100%)';
        el.style.opacity = '0.5';
        setTimeout(() => {
          el.style.transform = '';
          el.style.transition = '';
          el.style.opacity = '';
          onBack();
        }, 300);
      } else {
        // Cancel — spring back
        el.style.transition = SPRING;
        el.style.transform = 'translateX(0)';
        el.style.opacity = '1';
        setTimeout(() => {
          el.style.transform = '';
          el.style.transition = '';
          el.style.opacity = '';
        }, 300);
      }
      swiping = false;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.style.transform = '';
      el.style.transition = '';
      el.style.opacity = '';
    };
  }, [ref, onBack, enabled]);
}
